import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useDebounceValue } from '../../hooks/useDebounceValue';
import { useUserSession } from '../../models/hooks/appStateHooks';

// The singular route remains for clients that expect one page.
const PAGES_LOOKUP_URL_PATH = 'social/facebook/pagesLookup';
const FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX = /^[A-Za-z0-9.-]{1,50}$/;
const FACEBOOK_NUMERIC_ID_REGEX = /^\d+$/;

function isFacebookHost(hostname: string) {
	const normalizedHost = hostname.toLowerCase().replace(/^(?:www\.|m\.)/, '');
	return normalizedHost === 'facebook.com' || normalizedHost === 'fb.com';
}

function getPathSegments(pathname: string) {
	return pathname
		.split('/')
		.map(segment => {
			try {
				return decodeURIComponent(segment).trim();
			} catch {
				return segment.trim();
			}
		})
		.filter(Boolean);
}

function tryParseFacebookUrl(value: string) {
	const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;

	try {
		const parsed = new URL(candidate);
		return isFacebookHost(parsed.hostname) ? parsed : null;
	} catch {
		return null;
	}
}

export function normalizeFacebookPageHandle(handle?: string) {
	const candidate = (handle || '').trim().replace(/^@+/, '').trim();
	if (!candidate) {
		return '';
	}

	if (FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX.test(candidate)) {
		return candidate.toLowerCase();
	}

	const parsedUrl = tryParseFacebookUrl(candidate);
	if (!parsedUrl) {
		return candidate.toLowerCase();
	}

	const profileId = parsedUrl.searchParams.get('id')?.trim();
	if (profileId && FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX.test(profileId)) {
		return profileId.toLowerCase();
	}

	const pathSegments = getPathSegments(parsedUrl.pathname);
	if (!pathSegments.length) {
		return '';
	}

	const [firstSegment, secondSegment] = pathSegments;
	const normalizedFirstSegment = firstSegment.toLowerCase();
	const numericSegment = [...pathSegments].reverse().find(segment => FACEBOOK_NUMERIC_ID_REGEX.test(segment));
	if ((normalizedFirstSegment === 'pages' || normalizedFirstSegment === 'people') && numericSegment) {
		return numericSegment.toLowerCase();
	}

	if (normalizedFirstSegment === 'pg' && secondSegment && FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX.test(secondSegment)) {
		return secondSegment.toLowerCase();
	}

	return pathSegments.find(segment => FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX.test(segment))?.toLowerCase() || '';
}

export function useFacebookPageLookupQuery({
	handle,
	enabled = true,
	impersonationContext,
	forUserId,
}: {
	handle?: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string;
}) {
	const userSession = useUserSession();
	const normalizedHandle = normalizeFacebookPageHandle(handle);
	const debouncedHandle = useDebounceValue(normalizedHandle, 400);
	const effectiveImpersonationContext = Api.ImpersonationBroker.withForUser(
		impersonationContext || userSession.impersonationContext,
		forUserId
	);
	const accountId = effectiveImpersonationContext?.account?.id || userSession.account?.id;
	const userId = forUserId || effectiveImpersonationContext?.user?.id || userSession.user?.id;
	const shouldFetch = enabled && debouncedHandle.length >= 3 && FACEBOOK_PAGE_LOOKUP_TOKEN_REGEX.test(debouncedHandle);

	return useQuery({
		queryKey: [PAGES_LOOKUP_URL_PATH, accountId, userId, debouncedHandle],
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.IFacebookPageLookupResult[] | null>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: PAGES_LOOKUP_URL_PATH,
					queryParams: {
						forUserId,
						handle: debouncedHandle,
					},
					impersonationContext: effectiveImpersonationContext,
				}),
				'GET'
			);
		},
		enabled: shouldFetch,
		retry: false,
		staleTime: 60 * 60 * 1000,
	});
}
